import {
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
} from "@mui/material";

import type { ExpenseScope } from "../../lib/api/expenses";
import {
    EXPENSE_MONTHS,
    EXPENSE_SCOPE_ALL_STORES,
    EXPENSE_SCOPE_LOGISTICS,
    EXPENSE_SCOPE_ONLINE,
    EXPENSE_SCOPE_STORE,
    currentYear,
} from "./expenses.constants";

type ExpensesScopeBarProps = {
    scope: ExpenseScope;
    storeName: string;
    storeOptions: string[];
    year: number;
    statsMonths: number[];
    onScopeChange: (scope: ExpenseScope) => void;
    onStoreChange: (storeName: string) => void;
    onYearChange: (year: number) => void;
    onStatsMonthsChange: (months: number[], customized: boolean) => void;
};

export default function ExpensesScopeBar({
    scope,
    storeName,
    storeOptions,
    year,
    statsMonths,
    onScopeChange,
    onStoreChange,
    onYearChange,
    onStatsMonthsChange,
}: ExpensesScopeBarProps) {
    const yearOptions = Array.from(
        new Set([...Array.from({ length: 6 }, (_, index) => currentYear() - 3 + index), year]),
    ).sort((left, right) => left - right);
    const allMonths = EXPENSE_MONTHS.map((item) => item.month);

    /** При Онлайн/Логистика не ползваме "__all__", иначе Select не пали onChange при връщане към „Всички магазини“. */
    const storeSelectValue =
        scope === EXPENSE_SCOPE_STORE
            ? storeName || ""
            : scope === EXPENSE_SCOPE_ALL_STORES
              ? "__all__"
              : "__scope__";

    const storeSelectLabel =
        scope === EXPENSE_SCOPE_ONLINE
            ? "Онлайн"
            : scope === EXPENSE_SCOPE_LOGISTICS
              ? "Логистика"
              : scope === EXPENSE_SCOPE_STORE
                ? storeName || "Магазин"
                : "Всички магазини";

    return (
        <Stack direction="row" spacing={1.5} sx={{ mb: 1, flexWrap: "nowrap", alignItems: "center" }}>
            <TextField
                select
                size="small"
                label="Магазин"
                value={storeSelectValue}
                onChange={(event) => {
                    const next = String(event.target.value);

                    if (next === "__all__") {
                        onScopeChange(EXPENSE_SCOPE_ALL_STORES);
                        onStoreChange("");
                        return;
                    }

                    if (next === "__scope__") {
                        return;
                    }

                    onScopeChange(EXPENSE_SCOPE_STORE);
                    onStoreChange(next);
                }}
                sx={{ minWidth: 220 }}
                slotProps={{
                    select: {
                        renderValue: () => storeSelectLabel,
                    },
                }}
            >
                {scope === EXPENSE_SCOPE_ONLINE || scope === EXPENSE_SCOPE_LOGISTICS ? (
                    <MenuItem value="__scope__" sx={{ display: "none" }}>
                        {storeSelectLabel}
                    </MenuItem>
                ) : null}
                <MenuItem value="__all__">Всички магазини</MenuItem>
                {storeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </TextField>

            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "nowrap" }}>
                <Chip
                    label="Онлайн"
                    color={scope === EXPENSE_SCOPE_ONLINE ? "primary" : "default"}
                    variant={scope === EXPENSE_SCOPE_ONLINE ? "filled" : "outlined"}
                    onClick={() => {
                        if (scope === EXPENSE_SCOPE_ONLINE) {
                            onScopeChange(EXPENSE_SCOPE_ALL_STORES);
                            onStoreChange("");
                            return;
                        }

                        onScopeChange(EXPENSE_SCOPE_ONLINE);
                        onStoreChange("");
                    }}
                />
                <Chip
                    label="Логистика"
                    color={scope === EXPENSE_SCOPE_LOGISTICS ? "primary" : "default"}
                    variant={scope === EXPENSE_SCOPE_LOGISTICS ? "filled" : "outlined"}
                    onClick={() => {
                        if (scope === EXPENSE_SCOPE_LOGISTICS) {
                            onScopeChange(EXPENSE_SCOPE_ALL_STORES);
                            onStoreChange("");
                            return;
                        }

                        onScopeChange(EXPENSE_SCOPE_LOGISTICS);
                        onStoreChange("");
                    }}
                />
            </Stack>

            <FormControl sx={{ minWidth: 320 }}>
                <InputLabel id="expenses-stats-months-label">месеци</InputLabel>
                <Select
                    labelId="expenses-stats-months-label"
                    multiple
                    size="small"
                    value={statsMonths}
                    label="месеци"
                    onChange={(event) => {
                        const raw = (event.target.value as Array<number | string>).map(Number);
                        const picked = raw.filter((value) => value >= 1 && value <= 12);
                        const toggledWholeYear = raw.includes(-1);

                        if (toggledWholeYear) {
                            onStatsMonthsChange(statsMonths.length === 12 ? [] : allMonths, true);
                            return;
                        }

                        onStatsMonthsChange(picked, true);
                    }}
                    renderValue={(selected) => {
                        const next = selected as number[];
                        if (next.length === 0) {
                            return "Избери месеци";
                        }

                        if (next.length === 12) {
                            return "Цяла година";
                        }

                        return next
                            .slice()
                            .sort((a, b) => a - b)
                            .map((item) => EXPENSE_MONTHS.find((month) => month.month === item)?.label ?? item)
                            .join(", ");
                    }}
                >
                    <MenuItem value={-1}>
                        <Checkbox checked={statsMonths.length === 12} />
                        <ListItemText primary="Цяла година" />
                    </MenuItem>
                    {EXPENSE_MONTHS.map(({ month, label }) => (
                        <MenuItem key={month} value={month}>
                            <Checkbox checked={statsMonths.includes(month)} />
                            <ListItemText primary={label} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                select
                size="small"
                label="Година"
                value={year}
                onChange={(event) => onYearChange(Number(event.target.value))}
                sx={{ width: 140 }}
            >
                {yearOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </TextField>
        </Stack>
    );
}
